import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Trash2,
  ArrowLeft,
  Package,
  MapPin,
  Hash,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  LogIn,
} from 'lucide-react';
import Card from '../modern-components/ui/Card';
import Button from '../modern-components/ui/Button';
import { useQuoteCart } from '../context';
import { quoteAPI, BASE_URL } from '../services/api';

const getImage = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200&q=80';
  return url.startsWith('http') ? url : `${BASE_URL}${url}`;
};

const isAuthenticatedCustomer = () => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  if (!token || !userStr) return false;
  try {
    return JSON.parse(userStr).user_type === 'customer';
  } catch {
    return false;
  }
};

const QuoteCart = () => {
  const navigate = useNavigate();
  const { quoteCartItems, removeFromQuoteCart, updateQuoteQuantity, clearQuoteCart } = useQuoteCart();

  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  const authenticated = isAuthenticatedCustomer();

  const validate = () => {
    const errs = {};
    if (!address.trim()) errs.address = 'Delivery address is required';
    if (!pincode.trim()) errs.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(pincode.trim())) errs.pincode = 'Enter a valid 6-digit pincode';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!authenticated) {
      navigate('/login?redirect=/quote-cart');
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setErrors({});

    try {
      await quoteAPI.createQuote({
        items: quoteCartItems.map(item => ({
          product_id: item.product_id,
          brand_id: item.brand_id || null,
          size_id: item.size_id || null,
          size: item.size || null,
          height: item.height ?? null,
          width: item.width ?? null,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit || null,
        })),
        customer_notes: notes,
        delivery_address: address.trim(),
        pincode: pincode.trim(),
      });

      setSuccess(true);
      clearQuoteCart();
      setTimeout(() => {
        navigate('/dashboard?tab=quotes');
      }, 2000);
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h2>
          <p className="text-gray-600">Redirecting to your quotes...</p>
        </div>
      </div>
    );
  }

  if (quoteCartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16 animate-fade-in">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Quote Cart is Empty</h2>
            <p className="text-gray-600 mb-8">
              Browse products and click "Add to Quote" to request pricing on multiple products together.
            </p>
            <Link to="/products">
              <Button variant="primary" size="lg" leftIcon={<Package className="w-5 h-5" />}>
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate('/products')}
          className="mb-6 animate-fade-in"
        >
          Continue Shopping
        </Button>

        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Quote Cart</h1>
          <p className="text-lg text-gray-600">
            {quoteCartItems.length} product{quoteCartItems.length !== 1 && 's'} queued for a single combined quote request
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {quoteCartItems.map((item) => (
              <Card key={item.key} className="animate-slide-up">
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={getImage(item.product_image)}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <Link
                          to={`/products/${item.product_id}`}
                          className="font-semibold text-gray-900 hover:text-primary-600 transition-colors"
                        >
                          {item.product_name}
                        </Link>
                        {item.size && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Size: <span className="font-medium text-gray-700">{item.size}</span>
                            {(item.height || item.width) && (
                              <span> ({item.height || '—'} × {item.width || '—'})</span>
                            )}
                          </p>
                        )}
                        {item.brand_name && (
                          <p className="text-xs text-gray-500">
                            Brand: <span className="font-medium text-gray-700">{item.brand_name}</span>
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromQuoteCart(item.key)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      <label className="text-sm text-gray-500">Quantity:</label>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateQuoteQuantity(item.key, parseFloat(e.target.value) || 0)}
                        className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {item.unit && <span className="text-sm text-gray-500">{item.unit}</span>}
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Trash2 className="w-4 h-4" />}
                onClick={clearQuoteCart}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Clear Quote Cart
              </Button>
            </div>
          </div>

          {/* Submit form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-32">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Request Details</h2>

              {!authenticated ? (
                <div className="text-center py-6">
                  <LogIn className="w-10 h-10 text-primary-500 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-4">
                    Please login with your customer account to submit this quote request.
                  </p>
                  <Button
                    variant="primary"
                    fullWidth
                    leftIcon={<LogIn className="w-5 h-5" />}
                    onClick={() => navigate('/login?redirect=/quote-cart')}
                  >
                    Login to Continue
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {submitError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Delivery Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute top-3 left-3 w-4 h-4 text-gray-400 pointer-events-none" />
                      <textarea
                        rows={2}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Enter full delivery address"
                        disabled={submitting}
                        className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none disabled:bg-gray-100 ${errors.address ? 'border-red-400' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pincode <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Hash className="absolute inset-y-0 left-3 flex items-center w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={pincode}
                        onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit pincode"
                        disabled={submitting}
                        className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 ${errors.pincode ? 'border-red-400' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.pincode && <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute top-3 left-3 w-4 h-4 text-gray-400 pointer-events-none" />
                      <textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any special requirements or timeline..."
                        disabled={submitting}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none disabled:bg-gray-100"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled={submitting}
                    leftIcon={submitting ? null : <Send className="w-5 h-5" />}
                  >
                    {submitting ? 'Submitting...' : `Request Quote for ${quoteCartItems.length} Product${quoteCartItems.length !== 1 ? 's' : ''}`}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteCart;
